import React, { useState, useEffect, useCallback } from "react";
import {
  DollarSign,
  Plus,
  Trash2,
  Edit3,
  Save,
  X,
  CheckCircle2,
  XCircle,
  Clock,
  ArrowDownCircle,
  ArrowUpCircle,
  Loader2,
  AlertTriangle,
  Wallet,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { realEstateCasesApi, trustApi } from "../../services/api";
import PremiumModal from "../ui/PremiumModal";
import SearchableSelect, { SelectOption } from "../ui/SearchableSelect";
import { motion } from "framer-motion";

// ─── Types ──────────────────────────────────────────────────────────────────

interface Props {
  matterId: string;
  reCase: any;
  onUpdate: () => void;
}

interface Source {
  _id?: string;
  label: string;
  amountCents: number;
  sourceType: string;
  receivedInTrust: boolean;
  receivedDate: string;
}

interface Disbursement {
  _id?: string;
  label: string;
  payee: string;
  amountCents: number;
  method: string;
  category: string;
  released: boolean;
  notes?: string;
}

interface Holdback {
  _id?: string;
  label: string;
  amountCents: number;
  reason: string;
  released: boolean;
}

interface TrustEntry {
  _id: string;
  date: string;
  type: string;
  description: string;
  debitCents: number;
  creditCents: number;
  balanceCents: number;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const SOURCE_TYPES: SelectOption[] = [
  { value: "trust", label: "Trust" },
  { value: "wire", label: "Wire Transfer" },
  { value: "bank_draft", label: "Bank Draft" },
  { value: "mortgage_advance", label: "Mortgage Advance" },
  { value: "bridge_loan", label: "Bridge Loan" },
  { value: "other", label: "Other" },
];

const DISBURSEMENT_CATEGORIES: SelectOption[] = [
  { value: "mortgage_payout", label: "Mortgage Payout" },
  { value: "ltt", label: "Land Transfer Tax" },
  { value: "registration", label: "Registration" },
  { value: "commission", label: "Commission" },
  { value: "title_insurance", label: "Title Insurance" },
  { value: "legal_fees", label: "Legal Fees" },
  { value: "holdback", label: "Holdback" },
  { value: "tax_arrears", label: "Tax Arrears" },
  { value: "condo_arrears", label: "Condo Arrears" },
  { value: "law_society_levy", label: "Law Society Levy" },
  { value: "other", label: "Other" },
];

const DISBURSEMENT_METHODS: SelectOption[] = [
  { value: "wire", label: "Wire" },
  { value: "cheque", label: "Cheque" },
  { value: "eft", label: "EFT" },
  { value: "certified_cheque", label: "Certified Cheque" },
];

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatCAD = (cents: number) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(cents / 100);

const formatLabel = (s: string) =>
  s?.split("_").map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ") || "";

const formatDate = (d: string | null) =>
  d
    ? new Date(d).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" })
    : " - ";

const blankSource = (): Source => ({
  label: "",
  amountCents: 0,
  sourceType: "trust",
  receivedInTrust: false,
  receivedDate: "",
});

const blankDisbursement = (): Disbursement => ({
  label: "",
  payee: "",
  amountCents: 0,
  method: "wire",
  category: "other",
  released: false,
});

const blankHoldback = (): Holdback => ({
  label: "",
  amountCents: 0,
  reason: "",
  released: false,
});

// ─── Component ──────────────────────────────────────────────────────────────

const RealEstateFundsTab: React.FC<Props> = ({ matterId, reCase, onUpdate }) => {
  // Sub-tab state
  const [subTab, setSubTab] = useState<"plan" | "trust" | "disbursements">("plan");

  // Closing funds data
  const [sources, setSources] = useState<Source[]>([]);
  const [disbursements, setDisbursements] = useState<Disbursement[]>([]);
  const [holdbacks, setHoldbacks] = useState<Holdback[]>([]);

  // Editing states
  const [editingSource, setEditingSource] = useState<number | null>(null);
  const [editingDisbursement, setEditingDisbursement] = useState<number | null>(null);
  const [editingHoldback, setEditingHoldback] = useState<number | null>(null);

  // Trust ledger
  const [trustEntries, setTrustEntries] = useState<TrustEntry[]>([]);
  const [trustLoading, setTrustLoading] = useState(false);

  // UI state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [approvalNote, setApprovalNote] = useState("");
  const [approvingAction, setApprovingAction] = useState<"approve" | "reject" | null>(null);

  // ─── Initialize from reCase ─────────────────────────────────────────────

  useEffect(() => {
    const cf = reCase?.closingFunds;
    if (cf) {
      setSources(cf.sources?.length ? cf.sources.map((s: any) => ({ ...s })) : []);
      setDisbursements(cf.disbursements?.length ? cf.disbursements.map((d: any) => ({ ...d })) : []);
      setHoldbacks(cf.holdbacks?.length ? cf.holdbacks.map((h: any) => ({ ...h })) : []);
    }
  }, [reCase]);

  // ─── Fetch Trust Ledger ─────────────────────────────────────────────────

  const fetchTrustLedger = useCallback(async () => {
    setTrustLoading(true);
    try {
      const res = await trustApi.getSubledger({ matterId });
      setTrustEntries(res.data?.entries || res.data?.data || []);
    } catch (err) {
      console.error("Failed to load trust ledger:", err);
    } finally {
      setTrustLoading(false);
    }
  }, [matterId]);

  useEffect(() => {
    if (subTab === "trust") {
      fetchTrustLedger();
    }
  }, [subTab, fetchTrustLedger]);

  // ─── Totals ─────────────────────────────────────────────────────────────

  const totalSources = sources.reduce((sum, s) => sum + (s.amountCents || 0), 0);
  const totalDisbursements = disbursements.reduce((sum, d) => sum + (d.amountCents || 0), 0);
  const totalHoldbacks = holdbacks.reduce((sum, h) => sum + (h.amountCents || 0), 0);
  const balance = totalSources - totalDisbursements - totalHoldbacks;

  // ─── CRUD handlers ──────────────────────────────────────────────────────

  const addSource = () => {
    setSources([...sources, blankSource()]);
    setEditingSource(sources.length);
  };

  const removeSource = (idx: number) => {
    setSources(sources.filter((_, i) => i !== idx));
    if (editingSource === idx) setEditingSource(null);
  };

  const updateSource = (idx: number, field: keyof Source, value: any) => {
    setSources(sources.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  };

  const addDisbursement = () => {
    setDisbursements([...disbursements, blankDisbursement()]);
    setEditingDisbursement(disbursements.length);
  };

  const removeDisbursement = (idx: number) => {
    setDisbursements(disbursements.filter((_, i) => i !== idx));
    if (editingDisbursement === idx) setEditingDisbursement(null);
  };

  const updateDisbursement = (idx: number, field: keyof Disbursement, value: any) => {
    setDisbursements(disbursements.map((d, i) => (i === idx ? { ...d, [field]: value } : d)));
  };

  const addHoldback = () => {
    setHoldbacks([...holdbacks, blankHoldback()]);
    setEditingHoldback(holdbacks.length);
  };

  const removeHoldback = (idx: number) => {
    setHoldbacks(holdbacks.filter((_, i) => i !== idx));
    if (editingHoldback === idx) setEditingHoldback(null);
  };

  const updateHoldback = (idx: number, field: keyof Holdback, value: any) => {
    setHoldbacks(holdbacks.map((h, i) => (i === idx ? { ...h, [field]: value } : h)));
  };

  // ─── Save ───────────────────────────────────────────────────────────────

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await realEstateCasesApi.updateClosingFunds(reCase._id, {
        sources,
        disbursements,
        holdbacks,
      });
      setEditingSource(null);
      setEditingDisbursement(null);
      setEditingHoldback(null);
      setSuccessMsg("Closing funds saved");
      setTimeout(() => setSuccessMsg(null), 3000);
      onUpdate();
    } catch (err) {
      setError("Failed to save closing funds");
    } finally {
      setSaving(false);
    }
  };

  // ─── Approval ───────────────────────────────────────────────────────────

  const handleApproval = async (action: "approve" | "reject") => {
    setApprovingAction(action);
    try {
      if (action === "approve") {
        await realEstateCasesApi.approveClosingFunds(reCase._id, { note: approvalNote });
      } else {
        await realEstateCasesApi.rejectClosingFunds(reCase._id, { note: approvalNote });
      }
      setApprovalNote("");
      setSuccessMsg(action === "approve" ? "Closing funds approved" : "Closing funds rejected");
      setTimeout(() => setSuccessMsg(null), 3000);
      onUpdate();
    } catch (err) {
      setError(`Failed to ${action} closing funds`);
    } finally {
      setApprovingAction(null);
    }
  };

  // ─── Mark disbursement released ─────────────────────────────────────────

  const toggleDisbursementReleased = (idx: number) => {
    setDisbursements(
      disbursements.map((d, i) => (i === idx ? { ...d, released: !d.released } : d))
    );
  };

  // ─── Cents input helper ─────────────────────────────────────────────────

  const CentsInput: React.FC<{
    value: number;
    onChange: (cents: number) => void;
    className?: string;
  }> = ({ value, onChange, className = "" }) => (
    <input
      type="number"
      step="0.01"
      min="0"
      value={(value / 100).toFixed(2)}
      onChange={(e) => onChange(Math.round(parseFloat(e.target.value || "0") * 100))}
      className={`rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:border-emerald-600 text-right ${className}`}
    />
  );

  // ─── Render: Sub-tab pills ──────────────────────────────────────────────

  const renderSubTabs = () => (
    <div className="flex gap-2 mb-4">
      {([
        { key: "plan" as const, label: "Closing Funds Plan", icon: Wallet },
        { key: "trust" as const, label: "Trust Ledger", icon: DollarSign },
        { key: "disbursements" as const, label: "Disbursement Plan", icon: ArrowUpCircle },
      ]).map((t) => (
        <button
          key={t.key}
          onClick={() => setSubTab(t.key)}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
            subTab === t.key
              ? "bg-white shadow-sm border border-slate-200 text-slate-800"
              : "text-slate-500 hover:text-slate-700"
          }`}
        >
          <t.icon className="h-4 w-4" />
          {t.label}
        </button>
      ))}
    </div>
  );

  // ─── Render: Sources Table ──────────────────────────────────────────────

  const renderSourcesTable = () => (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-4">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2">
          <ArrowDownCircle className="h-4 w-4 text-emerald-500" />
          Fund Sources
        </h3>
        <button
          onClick={addSource}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-lg transition-colors hover:opacity-90"
          style={{ backgroundColor: "#0F5132" }}
        >
          <Plus className="h-3.5 w-3.5" /> Add Source
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-100">
              <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Label</th>
              <th className="text-right px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Amount</th>
              <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Type</th>
              <th className="text-center px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">In Trust</th>
              <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Received</th>
              <th className="text-right px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sources.length === 0 && (
              <tr>
                <td colSpan={6} className="px-5 py-8 text-center text-sm text-slate-400">
                  No fund sources added yet
                </td>
              </tr>
            )}
            {sources.map((s, i) => {
              const isEditing = editingSource === i;
              return (
                <tr key={i} className="border-b border-slate-50 hover:bg-slate-25">
                  <td className="px-5 py-2.5">
                    {isEditing ? (
                      <input
                        type="text"
                        value={s.label}
                        onChange={(e) => updateSource(i, "label", e.target.value)}
                        placeholder="e.g. Mortgage advance"
                        className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:border-emerald-600"
                      />
                    ) : (
                      <span className="text-sm text-slate-800">{s.label || " - "}</span>
                    )}
                  </td>
                  <td className="px-5 py-2.5 text-right">
                    {isEditing ? (
                      <CentsInput value={s.amountCents} onChange={(v) => updateSource(i, "amountCents", v)} className="w-32" />
                    ) : (
                      <span className="text-sm font-medium text-slate-800">{formatCAD(s.amountCents)}</span>
                    )}
                  </td>
                  <td className="px-5 py-2.5">
                    {isEditing ? (
                      <SearchableSelect
                        options={SOURCE_TYPES}
                        value={s.sourceType}
                        onChange={(v) => updateSource(i, "sourceType", v || "trust")}
                        placeholder="Type"
                        className="w-40"
                      />
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                        {formatLabel(s.sourceType)}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-2.5 text-center">
                    {isEditing ? (
                      <input
                        type="checkbox"
                        checked={s.receivedInTrust}
                        onChange={(e) => updateSource(i, "receivedInTrust", e.target.checked)}
                        className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                      />
                    ) : s.receivedInTrust ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 mx-auto" />
                    ) : (
                      <XCircle className="h-4 w-4 text-slate-300 mx-auto" />
                    )}
                  </td>
                  <td className="px-5 py-2.5">
                    {isEditing ? (
                      <input
                        type="date"
                        value={s.receivedDate ? s.receivedDate.slice(0, 10) : ""}
                        onChange={(e) => updateSource(i, "receivedDate", e.target.value)}
                        className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:border-emerald-600"
                      />
                    ) : (
                      <span className="text-sm text-slate-500">{formatDate(s.receivedDate)}</span>
                    )}
                  </td>
                  <td className="px-5 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {isEditing ? (
                        <button
                          onClick={() => setEditingSource(null)}
                          className="p-1.5 text-slate-400 hover:text-emerald-600 rounded-lg hover:bg-slate-50"
                          title="Done editing"
                        >
                          <Save className="h-3.5 w-3.5" />
                        </button>
                      ) : (
                        <button
                          onClick={() => setEditingSource(i)}
                          className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-50"
                          title="Edit"
                        >
                          <Edit3 className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => removeSource(i)}
                        className="p-1.5 text-slate-400 hover:text-red-500 rounded-lg hover:bg-red-50"
                        title="Remove"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t border-slate-200 bg-slate-50">
              <td className="px-5 py-3 text-sm font-bold text-slate-800">Total Sources</td>
              <td className="px-5 py-3 text-sm font-bold text-emerald-600 text-right">{formatCAD(totalSources)}</td>
              <td colSpan={4}></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );

  // ─── Render: Disbursements Table ────────────────────────────────────────

  const renderDisbursementsTable = () => (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-4">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2">
          <ArrowUpCircle className="h-4 w-4 text-red-500" />
          Disbursements
        </h3>
        <button
          onClick={addDisbursement}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-lg transition-colors hover:opacity-90"
          style={{ backgroundColor: "#0F5132" }}
        >
          <Plus className="h-3.5 w-3.5" /> Add Disbursement
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-100">
              <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Label</th>
              <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Payee</th>
              <th className="text-right px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Amount</th>
              <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Method</th>
              <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Category</th>
              <th className="text-center px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Released</th>
              <th className="text-right px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {disbursements.length === 0 && (
              <tr>
                <td colSpan={7} className="px-5 py-8 text-center text-sm text-slate-400">
                  No disbursements added yet
                </td>
              </tr>
            )}
            {disbursements.map((d, i) => {
              const isEditing = editingDisbursement === i;
              return (
                <tr key={i} className="border-b border-slate-50 hover:bg-slate-25">
                  <td className="px-5 py-2.5">
                    {isEditing ? (
                      <input
                        type="text"
                        value={d.label}
                        onChange={(e) => updateDisbursement(i, "label", e.target.value)}
                        placeholder="e.g. Existing mortgage payout"
                        className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:border-emerald-600"
                      />
                    ) : (
                      <span className="text-sm text-slate-800">{d.label || " - "}</span>
                    )}
                  </td>
                  <td className="px-5 py-2.5">
                    {isEditing ? (
                      <input
                        type="text"
                        value={d.payee}
                        onChange={(e) => updateDisbursement(i, "payee", e.target.value)}
                        placeholder="Payee name"
                        className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:border-emerald-600"
                      />
                    ) : (
                      <span className="text-sm text-slate-600">{d.payee || " - "}</span>
                    )}
                  </td>
                  <td className="px-5 py-2.5 text-right">
                    {isEditing ? (
                      <CentsInput value={d.amountCents} onChange={(v) => updateDisbursement(i, "amountCents", v)} className="w-32" />
                    ) : (
                      <span className="text-sm font-medium text-slate-800">{formatCAD(d.amountCents)}</span>
                    )}
                  </td>
                  <td className="px-5 py-2.5">
                    {isEditing ? (
                      <SearchableSelect
                        options={DISBURSEMENT_METHODS}
                        value={d.method}
                        onChange={(v) => updateDisbursement(i, "method", v || "wire")}
                        placeholder="Method"
                        className="w-36"
                      />
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                        {formatLabel(d.method)}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-2.5">
                    {isEditing ? (
                      <SearchableSelect
                        options={DISBURSEMENT_CATEGORIES}
                        value={d.category}
                        onChange={(v) => updateDisbursement(i, "category", v || "other")}
                        placeholder="Category"
                        className="w-44"
                      />
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                        {formatLabel(d.category)}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-2.5 text-center">
                    {d.released ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 mx-auto" />
                    ) : (
                      <Clock className="h-4 w-4 text-slate-300 mx-auto" />
                    )}
                  </td>
                  <td className="px-5 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {isEditing ? (
                        <button
                          onClick={() => setEditingDisbursement(null)}
                          className="p-1.5 text-slate-400 hover:text-emerald-600 rounded-lg hover:bg-slate-50"
                          title="Done editing"
                        >
                          <Save className="h-3.5 w-3.5" />
                        </button>
                      ) : (
                        <button
                          onClick={() => setEditingDisbursement(i)}
                          className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-50"
                          title="Edit"
                        >
                          <Edit3 className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => removeDisbursement(i)}
                        className="p-1.5 text-slate-400 hover:text-red-500 rounded-lg hover:bg-red-50"
                        title="Remove"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t border-slate-200 bg-slate-50">
              <td colSpan={2} className="px-5 py-3 text-sm font-bold text-slate-800">Total Disbursements</td>
              <td className="px-5 py-3 text-sm font-bold text-red-600 text-right">{formatCAD(totalDisbursements)}</td>
              <td colSpan={4}></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );

  // ─── Render: Holdbacks Table ────────────────────────────────────────────

  const renderHoldbacksTable = () => (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-4">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          Holdbacks
        </h3>
        <button
          onClick={addHoldback}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-lg transition-colors hover:opacity-90"
          style={{ backgroundColor: "#0F5132" }}
        >
          <Plus className="h-3.5 w-3.5" /> Add Holdback
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-100">
              <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Label</th>
              <th className="text-right px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Amount</th>
              <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Reason</th>
              <th className="text-center px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Released</th>
              <th className="text-right px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {holdbacks.length === 0 && (
              <tr>
                <td colSpan={5} className="px-5 py-8 text-center text-sm text-slate-400">
                  No holdbacks added yet
                </td>
              </tr>
            )}
            {holdbacks.map((h, i) => {
              const isEditing = editingHoldback === i;
              return (
                <tr key={i} className="border-b border-slate-50 hover:bg-slate-25">
                  <td className="px-5 py-2.5">
                    {isEditing ? (
                      <input
                        type="text"
                        value={h.label}
                        onChange={(e) => updateHoldback(i, "label", e.target.value)}
                        placeholder="e.g. Repair holdback"
                        className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:border-emerald-600"
                      />
                    ) : (
                      <span className="text-sm text-slate-800">{h.label || " - "}</span>
                    )}
                  </td>
                  <td className="px-5 py-2.5 text-right">
                    {isEditing ? (
                      <CentsInput value={h.amountCents} onChange={(v) => updateHoldback(i, "amountCents", v)} className="w-32" />
                    ) : (
                      <span className="text-sm font-medium text-slate-800">{formatCAD(h.amountCents)}</span>
                    )}
                  </td>
                  <td className="px-5 py-2.5">
                    {isEditing ? (
                      <input
                        type="text"
                        value={h.reason}
                        onChange={(e) => updateHoldback(i, "reason", e.target.value)}
                        placeholder="Reason for holdback"
                        className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:border-emerald-600"
                      />
                    ) : (
                      <span className="text-sm text-slate-600">{h.reason || " - "}</span>
                    )}
                  </td>
                  <td className="px-5 py-2.5 text-center">
                    {h.released ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 mx-auto" />
                    ) : (
                      <Clock className="h-4 w-4 text-slate-300 mx-auto" />
                    )}
                  </td>
                  <td className="px-5 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {isEditing ? (
                        <button
                          onClick={() => setEditingHoldback(null)}
                          className="p-1.5 text-slate-400 hover:text-emerald-600 rounded-lg hover:bg-slate-50"
                          title="Done editing"
                        >
                          <Save className="h-3.5 w-3.5" />
                        </button>
                      ) : (
                        <button
                          onClick={() => setEditingHoldback(i)}
                          className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-50"
                          title="Edit"
                        >
                          <Edit3 className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => removeHoldback(i)}
                        className="p-1.5 text-slate-400 hover:text-red-500 rounded-lg hover:bg-red-50"
                        title="Remove"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t border-slate-200 bg-slate-50">
              <td className="px-5 py-3 text-sm font-bold text-slate-800">Total Holdbacks</td>
              <td className="px-5 py-3 text-sm font-bold text-amber-600 text-right">{formatCAD(totalHoldbacks)}</td>
              <td colSpan={3}></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );

  // ─── Render: Totals Summary ─────────────────────────────────────────────

  const renderTotalsSummary = () => (
    <div className="bg-slate-50 rounded-xl border border-slate-200 p-5 grid grid-cols-4 gap-4 mb-4">
      <div className="text-center">
        <p className="text-xs text-slate-500 mb-1">Total Sources</p>
        <p className="text-lg font-bold text-emerald-600">{formatCAD(totalSources)}</p>
      </div>
      <div className="text-center">
        <p className="text-xs text-slate-500 mb-1">Total Disbursements</p>
        <p className="text-lg font-bold text-red-600">{formatCAD(totalDisbursements)}</p>
      </div>
      <div className="text-center">
        <p className="text-xs text-slate-500 mb-1">Holdbacks</p>
        <p className="text-lg font-bold text-amber-600">{formatCAD(totalHoldbacks)}</p>
      </div>
      <div className="text-center">
        <p className="text-xs text-slate-500 mb-1">Balance</p>
        <p className={`text-lg font-bold ${balance >= 0 ? "text-emerald-600" : "text-red-600"}`}>
          {formatCAD(balance)}
        </p>
      </div>
    </div>
  );

  // ─── Render: Approval Section ───────────────────────────────────────────

  const renderApprovalSection = () => {
    const approvals = reCase?.closingFunds?.approvals || [];
    const latestApproval = approvals.length > 0 ? approvals[approvals.length - 1] : null;

    return (
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
        <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          Approval Status
        </h3>

        {latestApproval && (
          <div className="mb-4 p-3 rounded-lg bg-slate-50 border border-slate-100">
            <div className="flex items-center gap-2 mb-1">
              {latestApproval.status === "approved" ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                  <CheckCircle2 className="h-3 w-3" /> Approved
                </span>
              ) : latestApproval.status === "rejected" ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                  <XCircle className="h-3 w-3" /> Rejected
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                  <Clock className="h-3 w-3" /> Pending
                </span>
              )}
              <span className="text-xs text-slate-400">
                {latestApproval.date ? formatDate(latestApproval.date) : ""}
              </span>
            </div>
            {latestApproval.note && (
              <p className="text-sm text-slate-600 mt-1">{latestApproval.note}</p>
            )}
            {latestApproval.approvedBy?.name && (
              <p className="text-xs text-slate-400 mt-1">by {latestApproval.approvedBy.name}</p>
            )}
          </div>
        )}

        {/* Approval history */}
        {approvals.length > 1 && (
          <div className="mb-4">
            <p className="text-xs text-slate-500 font-medium mb-2">History</p>
            <div className="space-y-1">
              {approvals.slice(0, -1).reverse().map((a: any, i: number) => (
                <div key={i} className="flex items-center gap-2 text-xs text-slate-400">
                  {a.status === "approved" ? (
                    <CheckCircle2 className="h-3 w-3 text-emerald-400" />
                  ) : (
                    <XCircle className="h-3 w-3 text-red-400" />
                  )}
                  <span>{formatLabel(a.status)}</span>
                  <span>{a.date ? formatDate(a.date) : ""}</span>
                  {a.note && <span className="text-slate-300">- {a.note}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Approve / Reject form */}
        <div className="flex flex-col gap-3">
          <textarea
            value={approvalNote}
            onChange={(e) => setApprovalNote(e.target.value)}
            placeholder="Optional note..."
            rows={2}
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 resize-none"
          />
          <div className="flex gap-2">
            <button
              onClick={() => handleApproval("approve")}
              disabled={approvingAction !== null}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-xl transition-colors hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: "#0F5132" }}
            >
              {approvingAction === "approve" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4" />
              )}
              Approve
            </button>
            <button
              onClick={() => handleApproval("reject")}
              disabled={approvingAction !== null}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-xl transition-colors hover:bg-red-100 disabled:opacity-50"
            >
              {approvingAction === "reject" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <XCircle className="h-4 w-4" />
              )}
              Reject
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ─── Render: Closing Funds Plan (sub-tab 1) ─────────────────────────────

  const renderClosingFundsPlan = () => (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
      {renderSourcesTable()}
      {renderDisbursementsTable()}
      {renderHoldbacksTable()}
      {renderTotalsSummary()}

      {/* Save button */}
      <div className="flex items-center justify-between mb-4">
        <div>
          {error && (
            <p className="text-sm text-red-600 flex items-center gap-1">
              <AlertTriangle className="h-4 w-4" /> {error}
            </p>
          )}
          {successMsg && (
            <p className="text-sm text-emerald-600 flex items-center gap-1">
              <CheckCircle2 className="h-4 w-4" /> {successMsg}
            </p>
          )}
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-white rounded-xl transition-colors hover:opacity-90 disabled:opacity-50"
          style={{ backgroundColor: "#0F5132" }}
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save Closing Funds
        </button>
      </div>

      {renderApprovalSection()}
    </motion.div>
  );

  // ─── Render: Trust Ledger (sub-tab 2) ───────────────────────────────────

  const renderTrustLedger = () => {
    if (trustLoading) {
      return (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex items-center justify-center py-16"
        >
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          <span className="ml-2 text-sm text-slate-500">Loading trust ledger...</span>
        </motion.div>
      );
    }

    let runningBalance = 0;

    return (
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-emerald-500" />
              Trust Ledger
            </h3>
            <button
              onClick={fetchTrustLedger}
              className="text-xs text-slate-500 hover:text-slate-700 transition-colors"
            >
              Refresh
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Date</th>
                  <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Type</th>
                  <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Description</th>
                  <th className="text-right px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Debit</th>
                  <th className="text-right px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Credit</th>
                  <th className="text-right px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Balance</th>
                </tr>
              </thead>
              <tbody>
                {trustEntries.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-5 py-12 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                          <DollarSign className="h-5 w-5 text-slate-400" />
                        </div>
                        <p className="text-sm text-slate-500">No trust entries for this matter</p>
                        <p className="text-xs text-slate-400">Trust receipts and disbursements will appear here</p>
                      </div>
                    </td>
                  </tr>
                )}
                {trustEntries.map((entry) => {
                  const debit = entry.debitCents || 0;
                  const credit = entry.creditCents || 0;
                  runningBalance += credit - debit;

                  return (
                    <tr key={entry._id} className="border-b border-slate-50 hover:bg-slate-25">
                      <td className="px-5 py-2.5 text-sm text-slate-600">{formatDate(entry.date)}</td>
                      <td className="px-5 py-2.5">
                        {entry.type === "receipt" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                            <ArrowDownCircle className="h-3 w-3" /> Receipt
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            <ArrowUpCircle className="h-3 w-3" /> Disbursement
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-2.5 text-sm text-slate-800">{entry.description}</td>
                      <td className="px-5 py-2.5 text-sm text-right text-red-600">
                        {debit > 0 ? formatCAD(debit) : " - "}
                      </td>
                      <td className="px-5 py-2.5 text-sm text-right text-emerald-600">
                        {credit > 0 ? formatCAD(credit) : " - "}
                      </td>
                      <td className="px-5 py-2.5 text-sm text-right font-medium text-slate-800">
                        {formatCAD(entry.balanceCents != null ? entry.balanceCents : runningBalance)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </motion.div>
    );
  };

  // ─── Render: Disbursement Plan (sub-tab 3) ──────────────────────────────

  const renderDisbursementPlan = () => {
    const releasedCount = disbursements.filter((d) => d.released).length;

    return (
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <ArrowUpCircle className="h-4 w-4 text-emerald-500" />
              Disbursement Plan
              <span className="text-xs font-normal text-slate-400">
                ({releasedCount}/{disbursements.length} released)
              </span>
            </h3>
            {releasedCount === disbursements.length && disbursements.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                <CheckCircle2 className="h-3 w-3" /> All Released
              </span>
            )}
          </div>

          {/* Progress bar */}
          {disbursements.length > 0 && (
            <div className="px-5 py-3 border-b border-slate-100">
              <div className="w-full bg-slate-100 rounded-full h-2">
                <div
                  className="h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${(releasedCount / disbursements.length) * 100}%`,
                    backgroundColor: "#0F5132",
                  }}
                />
              </div>
            </div>
          )}

          <div className="divide-y divide-slate-50">
            {disbursements.length === 0 && (
              <div className="px-5 py-12 text-center">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                    <ArrowUpCircle className="h-5 w-5 text-slate-400" />
                  </div>
                  <p className="text-sm text-slate-500">No disbursements planned</p>
                  <p className="text-xs text-slate-400">
                    Add disbursements in the Closing Funds Plan tab first
                  </p>
                </div>
              </div>
            )}
            {disbursements.map((d, i) => (
              <div
                key={i}
                className={`flex items-center gap-4 px-5 py-3.5 transition-colors ${
                  d.released ? "bg-emerald-50/50" : "hover:bg-slate-25"
                }`}
              >
                {/* Checkbox */}
                <button
                  onClick={() => {
                    toggleDisbursementReleased(i);
                  }}
                  className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                    d.released
                      ? "border-emerald-500 bg-emerald-500"
                      : "border-slate-300 hover:border-emerald-400"
                  }`}
                >
                  {d.released && <CheckCircle2 className="h-4 w-4 text-white" />}
                </button>

                {/* Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-sm font-medium ${
                        d.released ? "text-slate-400 line-through" : "text-slate-800"
                      }`}
                    >
                      {d.payee || d.label || "Unnamed disbursement"}
                    </span>
                    {d.label && d.payee && (
                      <span className="text-xs text-slate-400">{d.label}</span>
                    )}
                  </div>
                  {d.notes && <p className="text-xs text-slate-400 mt-0.5">{d.notes}</p>}
                </div>

                {/* Method badge */}
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 flex-shrink-0">
                  {formatLabel(d.method)}
                </span>

                {/* Category badge */}
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 flex-shrink-0">
                  {formatLabel(d.category)}
                </span>

                {/* Amount */}
                <span
                  className={`text-sm font-bold flex-shrink-0 ${
                    d.released ? "text-slate-400" : "text-slate-800"
                  }`}
                >
                  {formatCAD(d.amountCents)}
                </span>

                {/* Release button */}
                {!d.released && (
                  <button
                    onClick={() => toggleDisbursementReleased(i)}
                    className="flex-shrink-0 text-xs font-medium px-3 py-1.5 rounded-lg text-white transition-colors hover:opacity-90"
                    style={{ backgroundColor: "#0F5132" }}
                  >
                    Release
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Disbursement plan footer totals */}
          {disbursements.length > 0 && (
            <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
              <span className="text-sm font-bold text-slate-800">
                Total: {formatCAD(totalDisbursements)}
              </span>
              <div className="flex items-center gap-4">
                <span className="text-xs text-emerald-600">
                  Released: {formatCAD(disbursements.filter((d) => d.released).reduce((s, d) => s + d.amountCents, 0))}
                </span>
                <span className="text-xs text-amber-600">
                  Pending: {formatCAD(disbursements.filter((d) => !d.released).reduce((s, d) => s + d.amountCents, 0))}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Save after toggling released status */}
        <div className="flex justify-end mt-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-white rounded-xl transition-colors hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: "#0F5132" }}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save Changes
          </button>
        </div>
      </motion.div>
    );
  };

  // ─── Main Render ────────────────────────────────────────────────────────

  return (
    <div>
      {renderSubTabs()}

      {subTab === "plan" && renderClosingFundsPlan()}
      {subTab === "trust" && renderTrustLedger()}
      {subTab === "disbursements" && renderDisbursementPlan()}
    </div>
  );
};

export default RealEstateFundsTab;
